'use client';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useAccount } from '@/frontastic';
import { formatCurrency, formatDateMoment, getAgentName, getCookieValue } from '@/helpers/utils/siteUtils';
import { CreditHistory, PhoneRecordingPayload } from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { COOKIE_KEYS, DATE_FORMAT } from '../../../../../../constants/constant';
import MakePaymentModal from '../../../modals/customerDetailAction/makeAPayment';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../../ui/customAccordian/customAccordian';
import { Table, TableColumnConfig } from '../../../ui/customTable/Table';
import Loader from '../../../ui/loader';
import Pagination from '../../../utils/pagination';
import {
  CREDIT_HISTORY_HEADERS,
  CREDIT_HISTORY_ITEMS_PER_PAGE,
  CREDIT_HISTORY_KEYS,
  PAYLOAD_QUERY,
  RESPONSE_SUCCESS_MESSAGE,
} from '../../constants';
import { CreditTransaction, Customer, ShippingAddressDetailsType } from '../../types';

const CustomerCreditHistory = ({
  primaryEmail,
  customerDetails,
  billingAddress,
  creditAccountId,
  customerId,
  Texts,
}: {
  customerDetails: Customer;
  billingAddress: ShippingAddressDetailsType;
  creditAccountId: string;
  primaryEmail: string;
  customerId: string;
  Texts: any;
}) => {
  const { startPhoneRecording, stopPhoneRecording } = useAccount();
  const [value, setValue] = useState('');
  const params = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [creditTransactions, setCreditTransactions] = useState<CreditTransaction[]>([]);
  const { getCreditHistory } = useAccount();
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const startOrder = totalItems > 0 ? (currentPage - 1) * CREDIT_HISTORY_ITEMS_PER_PAGE + 1 : 0;
  const endOrder = Math.min(startOrder + CREDIT_HISTORY_ITEMS_PER_PAGE - 1, totalItems);

  const startRecording = async () => {
    const payload = {
      data: {
        agentEmailAddress: getAgentName(),
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as PhoneRecordingPayload;
    await startPhoneRecording(payload);
  };

  const stopRecording = async () => {
    const payload = {
      data: {
        agentEmailAddress: getAgentName(),
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as PhoneRecordingPayload;
    await stopPhoneRecording(payload);
  };

  const [open, setOpen] = useState(false);
  const creditHistoryCols: TableColumnConfig<any>[] = [
    {
      key: CREDIT_HISTORY_KEYS.DATE,
      header: CREDIT_HISTORY_HEADERS.DATE,
      render: (data) => <div>{formatDateMoment(data, DATE_FORMAT)}</div>,
    },
    {
      key: CREDIT_HISTORY_KEYS.STATUS,
      header: CREDIT_HISTORY_HEADERS.STATUS,
    },
    {
      key: CREDIT_HISTORY_KEYS.DESCRIPTION,
      header: CREDIT_HISTORY_HEADERS.DESCRIPTION,
    },
    {
      key: CREDIT_HISTORY_KEYS.PURCHASE,
      header: CREDIT_HISTORY_HEADERS.PURCHASE,
      render: (data) => <div>{formatCurrency(data)}</div>,
    },
    {
      key: CREDIT_HISTORY_KEYS.PAYMENT,
      header: CREDIT_HISTORY_HEADERS.PAYMENT,
      render: (data) => <div className="text-forestGreen">{formatCurrency(data)}</div>,
    },
    {
      key: CREDIT_HISTORY_KEYS.NEW_BALANCE,
      header: CREDIT_HISTORY_HEADERS.NEW_BALANCE,
      render: (data) => <div>{formatCurrency(data)}</div>,
    },
  ];

  const getHistory = async (pageNumber?: number) => {
    setLoading(true);
    if (creditAccountId) {
      const payload = {
        query: {
          creditAccountId: creditAccountId,
          divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
          pageNumber: pageNumber ?? currentPage,
          pageSize: CREDIT_HISTORY_ITEMS_PER_PAGE,
        },
      } as CreditHistory;
      await getCreditHistory(payload)
        .then((response) => {
          if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
            setCreditTransactions(response?.CreditTransactions);
            setTotalItems(response?.quantity ?? 0);
          }
        })
        .catch(() => setLoading(false));
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (value) {
      getHistory();
    }
  }, [value]);

  const onPageChange = async (page: number) => {
    setCurrentPage(page);
    await getHistory(page);
  };

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full rounded-md border border-foggyGray px-5 py-1 shadow-lg"
      onValueChange={setValue}
    >
      <AccordionItem value="creditHistory" className="border-none">
        <AccordionTrigger className={`${value === 'creditHistory' ? 'pb-0' : ''} text-lg font-bold hover:no-underline`}>
          {Texts.creditHistory}
        </AccordionTrigger>
        <AccordionContent className="pb-5">
          <section className="flex flex-col items-start justify-start">
            {value === 'creditHistory' && (
              <p className="mr-auto text-sm font-normal">
                {Texts.showingTransactions} {startOrder}-{endOrder} of {totalItems}
              </p>
            )}
          </section>
          {loading && <Loader />}
          <section className="mb-4 flex justify-end">
            <button
              className={'h-[30px] rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium'}
              onClick={() => {
                stopRecording();
                setOpen(true);
              }}
            >
              {Texts.makeAPayment}
            </button>
            {open && (
              <MakePaymentModal
                open={open}
                setOpen={setOpen}
                customerDetails={customerDetails}
                primaryEmail={primaryEmail}
                creditAccountId={customerId}
                billingAddress={billingAddress}
                Texts={Texts}
                getHistory={getHistory}
                startRecording={startRecording}
              />
            )}
          </section>
          <Table columns={creditHistoryCols} data={creditTransactions} designTable={false} />
          {creditTransactions?.length === 0 || !creditTransactions ? Texts.noRecordsFound : ''}
          {totalItems > 5 && (
            <section className="mt-4 flex w-full items-center justify-end">
              <Pagination
                currentPage={currentPage}
                itemsPerPage={CREDIT_HISTORY_ITEMS_PER_PAGE}
                totalItems={totalItems}
                onPageChange={onPageChange}
              ></Pagination>
            </section>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};

export default CustomerCreditHistory;
